import type { ReactNode } from "react";
import Loading from "@/shared/components/Loading";
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { GlobalPermission } from "@superagents/types/api";
import type { PlanFeatureKey } from "@/core/hooks/planFeature";
import { usePlanFeatureAccess } from "@/core/hooks/usePlanAccess";
import { usePlanGateUpgrade } from "@/features/billing/hooks/usePlanGateUpgrade";
import type { PlanUpgradeOffer } from "@/features/billing/utils/planUpgradeOffer";
import { useActiveCompanyAdmin } from "@/features/settings/hooks/useActiveCompanyAdmin";
import { RoutePaths } from "../constants";
import { FeatureGate } from "./FeatureGate";
import { PermissionsErrorState } from "./PermissionsErrorState";
import {
  LOCKABLE_PAGE_COPY,
  pagePlanLocksPath,
  type LockablePagePermission,
} from "./FeatureGate/pageCopy";
import { planFeatureForPath, resolveGateReturn } from "./gateReturn";
import { useLastAccessiblePageStore } from "./useLastAccessiblePage";
import { useSectorPermissions, type SectorAdmin } from "./useSectorPermissions";

type RoutePath = (typeof RoutePaths)[keyof typeof RoutePaths];

type PermissionGateProps =
  | {
      /**
       * Chave da página: o bloqueio mostra o nome e a frase dela. A trava de
       * plano também vem da página (`planFeature` em LOCKABLE_PAGE_COPY), a
       * mesma fonte do menu e da origem do "Voltar para ...": fora do plano, a
       * rota mostra o bloqueio de plano antes de olhar a permissão (SA-1401).
       * Com `planScope: "actions"` (Integrações), só as rotas de ação da
       * página mostram o bloqueio de plano; as demais abrem (SA-1360 P4-C).
       */
      requiredPermission: LockablePagePermission;
      /** "page" (padrão): renderiza o bloqueio de recurso no lugar da página. */
      fallback?: "page";
      redirectTo?: never;
    }
  | {
      /** Gate de ação dentro de uma página (ex.: instalar integração). */
      requiredPermission: GlobalPermission;
      fallback: "redirect";
      redirectTo: RoutePath;
    };

type PlanLock = {
  feature: PlanFeatureKey;
  offer: PlanUpgradeOffer;
  isCompanyAdmin: boolean;
};

type BlockedPageProps = {
  pageKey: LockablePagePermission;
  permissions: readonly GlobalPermission[];
  admins: readonly SectorAdmin[];
  /** Presente: bloqueio de plano. Ausente: bloqueio de permissão. */
  planLock?: PlanLock;
};

/* Só a página bloqueada assina a origem: a liberada não re-renderiza a cada
   troca de rota que o MainLayout grava. */
function BlockedPage({
  pageKey,
  permissions,
  admins,
  planLock,
}: BlockedPageProps) {
  const navigate = useNavigate();
  // Origem gravada pelo MainLayout (useTrackLastAccessiblePage).
  const lastAccessiblePath = useLastAccessiblePageStore((state) => state.path);
  // A origem é conferida de novo com as permissões e o plano de agora; plano
  // ainda pendente conta como travado.
  const originPlanAccess = usePlanFeatureAccess(
    lastAccessiblePath ? planFeatureForPath(lastAccessiblePath) : null
  );
  // A saída volta para a última página liberada; sem ela, Conversas.
  const gateReturn = resolveGateReturn(
    lastAccessiblePath,
    permissions,
    originPlanAccess.status !== "allowed"
  );
  const close = () => navigate(gateReturn.to);

  if (planLock) {
    return (
      <PlanBlockedCard
        pageKey={pageKey}
        admins={admins}
        planLock={planLock}
        returnLabel={gateReturn.label}
        onClose={close}
      />
    );
  }

  return (
    <FeatureGate
      pageKey={pageKey}
      admins={admins}
      returnLabel={gateReturn.label}
      onClose={close}
    />
  );
}

type PlanBlockedCardProps = {
  pageKey: LockablePagePermission;
  admins: readonly SectorAdmin[];
  planLock: PlanLock;
  returnLabel: string;
  onClose: () => void;
};

/**
 * Bloqueio de plano. O primário do admin da empresa roda a mesma ação de troca
 * de plano do bloco de uso da aba Faturamento: Portal só com `can_self_upgrade`
 * e plano mínimo nomeado, senão Vendas. Rótulo e corpo vêm do mesmo caminho,
 * para dizer o que o clique faz; enquanto o caminho depende do resumo do
 * billing, o admin vê Loading (nem o rótulo nem o corpo trocam na frente dele).
 */
function PlanBlockedCard({
  pageKey,
  admins,
  planLock,
  returnLabel,
  onClose,
}: PlanBlockedCardProps) {
  const { feature, offer, isCompanyAdmin } = planLock;
  const upgradeAction = usePlanGateUpgrade({
    feature,
    featureLabel: LOCKABLE_PAGE_COPY[pageKey].label,
    requiredPlanName: offer.requiredPlanName,
    enabled: isCompanyAdmin,
  });

  if (isCompanyAdmin && upgradeAction.isPathPending) return <Loading />;

  return (
    <FeatureGate
      reason="plan"
      pageKey={pageKey}
      isAdmin={isCompanyAdmin}
      planNow={offer.currentPlanName}
      planRequired={offer.requiredPlanName}
      unlocks={offer.unlocks}
      admins={admins}
      returnLabel={returnLabel}
      onClose={onClose}
      onPrimary={() => void upgradeAction.upgrade("plan_block")}
      primaryLabel={upgradeAction.primaryLabel}
      salesPath={upgradeAction.salesPath}
      primaryLoading={upgradeAction.isLoading}
      primaryError={upgradeAction.error}
    />
  );
}

type PlanGateProps = {
  feature: PlanFeatureKey;
  pageKey: LockablePagePermission;
  permissions: readonly GlobalPermission[];
  admins: readonly SectorAdmin[];
  /** O que a rota mostra quando o plano libera (página ou bloqueio de permissão). */
  children: ReactNode;
};

/**
 * Trava de plano da rota, antes da permissão (`featureState` do protótipo:
 * fora do plano é o motivo, com ou sem a chave). Enquanto o plano não é
 * conhecido, Loading: nem o bloqueio pisca, nem a página monta.
 */
function PlanGate({
  feature,
  pageKey,
  permissions,
  admins,
  children,
}: PlanGateProps) {
  const planAccess = usePlanFeatureAccess(feature);
  // Admin da empresa é quem pode fazer o upgrade: decide copy e botões, então
  // o bloqueio espera por ele em vez de trocar de ramo na frente da pessoa.
  const { isCompanyAdmin, isLoading: isCompanyAdminLoading } =
    useActiveCompanyAdmin();

  if (planAccess.status === "pending") return <Loading />;
  if (planAccess.status === "allowed") return <>{children}</>;
  // Plano desconhecido (access-state falhou): erro recuperável, nunca "não
  // está no plano" (SA-1361, ruling F2-7). A página também não monta: as rotas
  // dela respondem 403 de plano se o plano de fato não tiver o recurso.
  if (planAccess.status === "error") {
    return (
      <PermissionsErrorState
        subject="plan"
        onRetry={planAccess.retry}
        isRetrying={planAccess.isRetrying}
      />
    );
  }
  // Travado pelo access-state, com a oferta (nomes do catálogo) a caminho:
  // espera em vez de mostrar a copy genérica e trocar de texto logo depois.
  if (!planAccess.offer || isCompanyAdminLoading) return <Loading />;

  return (
    <BlockedPage
      pageKey={pageKey}
      permissions={permissions}
      admins={admins}
      planLock={{ feature, offer: planAccess.offer, isCompanyAdmin }}
    />
  );
}

const PermissionGate = (props: PermissionGateProps) => {
  const {
    permissions,
    sectorAdmins,
    isLoading,
    isPlaceholder,
    isError,
    isFetching,
    refetch,
  } = useSectorPermissions();
  const { pathname } = useLocation();
  // O gate é uma rota sem elemento próprio no meio da árvore: o `<Outlet>` dele
  // repassa o contexto do layout de cima. Sem isso, a página abaixo de um gate
  // aninhado (Áreas, dentro do layout da Equipe) recebia `undefined` no
  // `useOutletContext` (SA-1367). Gate de topo repassa `undefined`, como antes.
  const outletContext = useOutletContext();

  // Falha sem dado do setor ativo (SA-1360): `permissions` vazio aqui é falta
  // de resposta, não de acesso, e o bloqueio diria ao admin que ele não pode
  // nada. Vem ANTES do loading: no retry o hook volta a `isLoading` (ou traz o
  // placeholder de outro setor) e o card precisa ficar, com o botão em espera.
  if (isError) {
    return (
      <PermissionsErrorState
        onRetry={() => void refetch()}
        isRetrying={isFetching}
      />
    );
  }

  // Primeiro carregamento, ou 1ª visita a um setor (o hook ainda traz o dado
  // do setor anterior): decidir com ele montaria a página do setor novo com
  // as permissões do antigo.
  if (isLoading || isPlaceholder) {
    return <Loading />;
  }

  const hasPermission = permissions.includes(props.requiredPermission);

  if (props.fallback === "redirect") {
    return hasPermission ? (
      <Outlet context={outletContext} />
    ) : (
      <Navigate
        to={props.redirectTo}
        replace
      />
    );
  }

  // Página sem permissão aparece bloqueada, nunca some (SA-1352 D10). Nunca é
  // a página real ao fundo: montá-la dispararia as rotas que respondem 403.
  const permissionOutcome = hasPermission ? (
    <Outlet context={outletContext} />
  ) : (
    <BlockedPage
      pageKey={props.requiredPermission}
      permissions={permissions}
      admins={sectorAdmins}
    />
  );

  const page = LOCKABLE_PAGE_COPY[props.requiredPermission];
  const planFeature = page.planFeature;
  // Integrações (`planScope: "actions"`): o catálogo e o detalhe abrem fora
  // do plano; só a rota de instalar mostra o bloqueio de plano, que vence a
  // permissão como nas outras páginas.
  if (!planFeature || !pagePlanLocksPath(page, pathname)) {
    return permissionOutcome;
  }

  return (
    <PlanGate
      feature={planFeature}
      pageKey={props.requiredPermission}
      permissions={permissions}
      admins={sectorAdmins}>
      {permissionOutcome}
    </PlanGate>
  );
};

export default PermissionGate;

export { usePermission } from "./usePermission";
